import { format } from 'date-fns';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Modal } from '../components/Modal';
import {
  useWaitlist,
  type WaitlistCounts,
  type WaitlistEntry,
  type WaitlistStatus,
} from '../hooks/useAdminData';
import { useDebounce } from '../hooks/useDebounce';
import { deriveWaitlistSource, type WaitlistAttribution } from '../lib/waitlistSource';

const PAGE_SIZE = 50;

const STATUSES: WaitlistStatus[] = [
  'waiting',
  'invited',
  'activated',
  'converted',
  'reclaimed',
  'declined',
];

/**
 * Colour carries the meaning an operator scans for: converted is the outcome
 * the programme exists for, reclaimed is a place taken back, and declined is a
 * door closed on purpose. Waiting is deliberately quiet — it is the default
 * state of most of the queue and should not compete for attention.
 */
const STATUS_STYLE: Record<WaitlistStatus, string> = {
  waiting: 'bg-surface-hover text-text-secondary',
  invited: 'bg-primary/15 text-primary',
  activated: 'bg-info/15 text-info',
  converted: 'bg-success/15 text-success',
  reclaimed: 'bg-warning/15 text-warning',
  declined: 'bg-danger/15 text-danger',
};

function StatusBadge({ status }: { status: WaitlistStatus }) {
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_STYLE[status]}`}
    >
      {status}
    </span>
  );
}

const countOf = (counts: WaitlistCounts | undefined, status: WaitlistStatus) =>
  counts?.[status] ?? 0;

const when = (value: string | null) => (value ? format(new Date(value), 'PPp') : '—');

function WaitlistDetail({ entry }: { entry: WaitlistEntry }) {
  const hasMetadata = entry.metadata && Object.keys(entry.metadata).length > 0;
  const attribution = (entry.metadata ?? {}) as WaitlistAttribution;
  const utm = attribution.utm ?? {};

  return (
    <div className="space-y-4 text-sm">
      <Field label="Email" value={entry.email} />
      <Field label="Full Name" value={entry.fullName ?? '—'} />
      <Field label="Company" value={entry.company ?? '—'} />
      <Field label="Phone" value={entry.phone ?? '—'} />
      <Field label="Joined" value={format(new Date(entry.createdAt), 'PPpp')} />
      <Field label="Status" value={entry.status} />
      <Field label="Invited" value={when(entry.invitedAt)} />
      <Field label="Invite expires" value={when(entry.inviteExpiresAt)} />
      <Field label="Activated" value={when(entry.activatedAt)} />
      <Field label="Reminder sent" value={when(entry.reminderSentAt)} />
      <Field
        label="Reclaimed"
        value={
          entry.reclaimedAt
            ? `${when(entry.reclaimedAt)}${entry.reclaimReason ? ` (${entry.reclaimReason})` : ''}`
            : '—'
        }
      />
      <Field label="Converted" value={when(entry.convertedAt)} />
      <Field label="Organization" value={entry.organizationId ?? '—'} />
      <Field label="Source" value={deriveWaitlistSource(entry.metadata)} />
      <Field label="UTM Medium" value={utm.medium ?? '—'} />
      <Field label="UTM Campaign" value={utm.campaign ?? '—'} />
      <Field label="Referrer" value={attribution.referrer ?? '—'} />
      <Field label="Landing Page" value={attribution.landingPage ?? '—'} />
      <Field label="IP" value={attribution.ip ?? '—'} />
      <Field label="User Agent" value={attribution.userAgent ?? '—'} />
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-text-muted mb-1.5">
          Metadata
        </p>
        {hasMetadata ? (
          <pre className="bg-surface-hover border border-border rounded-lg p-3 text-xs text-text-secondary overflow-x-auto">
            {JSON.stringify(entry.metadata, null, 2)}
          </pre>
        ) : (
          <span className="text-text-muted italic">No metadata</span>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-xs font-medium uppercase tracking-wider text-text-muted">{label}</span>
      <span className="text-text-secondary text-right break-all">{value}</span>
    </div>
  );
}

export function Waitlist() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<WaitlistEntry | null>(null);
  const [status, setStatus] = useState<WaitlistStatus | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  const params = useMemo(() => {
    const p: Record<string, unknown> = { page, limit: PAGE_SIZE };
    if (debouncedSearch) p.search = debouncedSearch;
    if (status) p.status = status;
    return p;
  }, [page, debouncedSearch, status]);

  const { data, isLoading } = useWaitlist(params);

  const entries = data?.data ?? [];
  const total = data?.total ?? 0;
  const counts = data?.counts;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Waitlist</h1>
        <p className="text-sm text-text-muted mt-0.5">
          People who signed up to join, newest first. {total} total.
        </p>
      </div>

      {/* The tally counts the whole queue, not the filtered page: its job is to
          show the shape of the queue, which a filter would hide. */}
      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={status === s}
            onClick={() => {
              setStatus(status === s ? null : s);
              setPage(1);
            }}
            className={`rounded-lg border px-3 py-1.5 text-sm transition-colors ${
              status === s ? 'border-primary text-primary' : 'border-border text-text-secondary'
            }`}
          >
            <span className="capitalize">{s}</span>
            <span className="ml-2 tabular-nums text-text-muted">{countOf(counts, s)}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            placeholder="Search by email, name or company..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-9 pr-4 py-2 bg-surface border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="bg-surface rounded-xl border border-border p-4 animate-pulse space-y-2">
          <div className="h-4 bg-border rounded w-full" />
          <div className="h-4 bg-border rounded w-3/4" />
          <div className="h-4 bg-border rounded w-5/6" />
        </div>
      ) : entries.length === 0 ? (
        <div className="bg-surface rounded-xl border border-border px-5 py-8 text-center text-text-muted text-sm">
          No waitlist signups found
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-text-muted">
                  <th className="px-5 py-2.5 font-medium text-xs">Status</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Name</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Email</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Company</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Phone</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Source</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Joined</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr
                    key={entry.id}
                    className="border-b border-border last:border-b-0 hover:bg-surface-hover transition-colors cursor-pointer"
                    onClick={() => setSelected(entry)}
                  >
                    <td className="px-5 py-2.5">
                      <StatusBadge status={entry.status} />
                    </td>
                    <td className="px-5 py-2.5">
                      <span className="text-text font-medium">
                        {entry.fullName ?? <span className="text-text-muted italic">—</span>}
                      </span>
                    </td>
                    <td className="px-5 py-2.5">
                      <span className="text-text-secondary">{entry.email}</span>
                    </td>
                    <td className="px-5 py-2.5">
                      <span className="text-text-secondary">
                        {entry.company ?? <span className="text-text-muted">—</span>}
                      </span>
                    </td>
                    <td className="px-5 py-2.5">
                      <span className="text-text-secondary">
                        {entry.phone ?? <span className="text-text-muted">—</span>}
                      </span>
                    </td>
                    <td className="px-5 py-2.5">
                      <span className="text-text-secondary">
                        {deriveWaitlistSource(entry.metadata)}
                      </span>
                    </td>
                    <td className="px-5 py-2.5">
                      <span className="text-text-secondary">
                        {format(new Date(entry.createdAt), 'MMM d, yyyy')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-5 py-3 text-sm text-text-secondary border-t border-border">
            <span>
              {total > 0
                ? `${(page - 1) * PAGE_SIZE + 1}-${Math.min(page * PAGE_SIZE, total)} of ${total}`
                : '0 results'}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1}
                className="px-3 py-1 rounded-md hover:bg-surface-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Prev
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1 rounded-md hover:bg-surface-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal open={!!selected} onClose={() => setSelected(null)} title="Waitlist Signup" size="md">
        {selected && <WaitlistDetail entry={selected} />}
      </Modal>
    </div>
  );
}
